import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Copy, ShieldCheck, Activity, Settings, ScrollText, Users, FlaskConical, Link2, ChevronDown, ShieldBan } from "lucide-react";
import { ListaNegra } from "@/components/admin/guard/golpe-modulos";
import { toast } from "sonner";

import { AdminGate } from "@/components/admin/AdminGate";
import { AdminShell, AdminSection, AdminCard, AdminStat } from "@/components/admin/shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { disconnectGuard, getGuardQr, getGuardStatus, listGuardGroups, saveGuardConfig } from "@/lib/guard/guard.functions";
import { AutoWebhookButton, EquipeGuard, MensagensGuard, SimuladorCenarios, TesteReal, TiposDeGolpe } from "@/components/admin/guard/GuardRobo";
import { GUARD_BOT_NUMBERS, type GuardConfig, type GuardMode } from "@/lib/guard/rules";
import { LogCompleto, Ocorrencias, RegrasExplicadas } from "@/components/admin/guard/GuardPainel";
import { RemovidosEngano } from "@/components/admin/guard/RemovidosEngano";
import { VotantesEnquete } from "@/components/admin/guard/VotantesEnquete";

export const Route = createFileRoute("/admin/feo-guard")({
  head: () => ({ meta: [
    { title: "Admin — FEO Guard" },
    { name: "description", content: "Monitoramento e gestão das ocorrências de segurança dos grupos da FEO." },
    { property: "og:title", content: "Admin — FEO Guard" },
    { property: "og:description", content: "Monitoramento e gestão das ocorrências de segurança dos grupos da FEO." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
    { name: "robots", content: "noindex,nofollow" },
  ] }),
  component: () => (
    <AdminGate route="/admin/feo-guard">
      <GuardPage />
    </AdminGate>
  ),
});

const MODES: { id: GuardMode; label: string; desc: string }[] = [
  { id: "remover", label: "Apaga e remove", desc: "Golpe: apaga para todos e remove o autor." },
  { id: "apagar", label: "Só apaga", desc: "Apaga a mensagem; remoção fica com o admin." },
  { id: "avisar", label: "Só avisa", desc: "Não mexe no grupo." },
];

const lines = (s: string) => s.split("\n").map((l) => l.trim()).filter(Boolean);

function GuardPage() {
  const load = useServerFn(getGuardStatus);
  const q = useQuery({ queryKey: ["admin", "guard"], queryFn: () => load(), refetchInterval: 15000 });
  const d = q.data;
  const hoje = new Date().toDateString();
  const evHoje = d?.events.filter((e) => e.status !== "falso-alarme" && new Date(e.created_at).toDateString() === hoje) ?? [];

  return (
    <AdminShell title="FEO Guard" description="Central de segurança dos grupos" width="7xl" icon={<ShieldCheck className="h-7 w-7 text-primary" />}>
      {q.isLoading && <p className="text-sm text-muted-foreground">Carregando...</p>}
      {q.error && <p className="text-sm text-destructive">{String(q.error)}</p>}
      {d && (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <AdminStat label="ADMIN (moderador)" value={d.connection.connected ? "Conectado" : "Desconectado"} hint={d.connection.detail} />
            <AdminStat label="SPY (espião)" value={d.espiao.connected ? "Conectado" : "Desconectado"} hint={d.espiao.detail} />
            <AdminStat label="Anti-Hacker" value={d.cfg.enabled && d.cfg.antiHacker ? "Ativado" : "Desativado"} hint={MODES.find((m) => m.id === d.cfg.mode)?.label} />
            <AdminStat label="Golpes pendentes" value={d.events.filter((e) => e.verdict === "golpe" && e.status === "novo").length} hint={`${evHoje.filter((e) => e.verdict === "golpe").length} golpes hoje · ${d.teamCount} números da equipe`} />
          </div>

          <Tabs defaultValue="monitor" className="mt-4">
            <TabsList className="grid h-auto w-full grid-cols-4 gap-1 bg-secondary p-1 sm:w-fit">
              <TabsTrigger value="monitor" className="gap-2 px-2 py-3 sm:px-5"><Activity className="hidden h-4 w-4 sm:block" />Monitoramento</TabsTrigger>
              <TabsTrigger value="regras" className="gap-2 px-2 py-3 sm:px-5"><ShieldCheck className="hidden h-4 w-4 sm:block" />Anti-Hacker</TabsTrigger>
              <TabsTrigger value="config" className="gap-2 px-2 py-3 sm:px-5"><Settings className="hidden h-4 w-4 sm:block" />Configurações</TabsTrigger>
              <TabsTrigger value="log" className="gap-2 px-2 py-3 sm:px-5"><ScrollText className="hidden h-4 w-4 sm:block" />Log completo</TabsTrigger>
            </TabsList>
            <TabsContent value="monitor" className="mt-8 space-y-8">
              <AdminSection label="Ocorrências"><Ocorrencias events={d.events} /></AdminSection>
              <AdminSection label="Mensagens recebidas"><MensagensGuard /></AdminSection>
              <AdminSection label="Pessoas removidas por engano"><RemovidosEngano /></AdminSection>
              <AdminSection label="Votos nas enquetes (para o CRM)"><VotantesEnquete /></AdminSection>
            </TabsContent>
            <TabsContent value="log" className="mt-8 space-y-8">
              <AdminSection label="Log completo"><LogCompleto /></AdminSection>
            </TabsContent>
            <TabsContent value="regras" className="mt-8 space-y-8">
              <AdminSection label="Regras: como o robô funciona"><RegrasExplicadas /></AdminSection>
              <AdminSection label="Anti-Hacker (grupo fechado)">
                <AntiHacker cfg={d.cfg} moderador={d.connection.connected} espiao={d.espiao.connected} />
              </AdminSection>
              <AdminSection label="Tipos de golpe e proteções"><TiposDeGolpe cfg={d.cfg} /></AdminSection>
            </TabsContent>
            <TabsContent value="config" className="mt-8 space-y-8">
              <AdminSection label="Operação e grupos"><Config cfg={d.cfg} /></AdminSection>
              <details className="border-t border-border py-5">
                <summary className="flex cursor-pointer list-none items-center gap-3 font-semibold"><Link2 className="h-5 w-5 text-primary" />Conexões ADMIN e SPY<ChevronDown className="ml-auto h-4 w-4 text-muted-foreground" /></summary>
                <div className="mt-6 grid gap-4 md:grid-cols-2">
                  <Conexao papel="moderador" nome="ADMIN · moderador" desc="Administrador dos grupos. Executa as ações de proteção." st={d.connection} />
                  <Conexao papel="espiao" nome="SPY · espião" desc="Membro dos grupos. Observa e permite comparar as mensagens recebidas." st={d.espiao} />
                </div>
                {d.connection.configured && <div className="mt-4"><AutoWebhookButton /></div>}
              </details>
              <details className="border-t border-border py-5">
                <summary className="flex cursor-pointer list-none items-center gap-3 font-semibold"><Users className="h-5 w-5 text-primary" />Equipe autorizada<ChevronDown className="ml-auto h-4 w-4 text-muted-foreground" /></summary>
                <div className="mt-6"><EquipeGuard /></div>
              </details>
              <details className="border-t border-border py-5">
                <summary className="flex cursor-pointer list-none items-center gap-3 font-semibold"><ShieldBan className="h-5 w-5 text-primary" />Lista negra<ChevronDown className="ml-auto h-4 w-4 text-muted-foreground" /></summary>
                <div className="mt-6"><ListaNegra /></div>
              </details>
              <details className="border-t border-border py-5">
                <summary className="flex cursor-pointer list-none items-center gap-3 font-semibold"><FlaskConical className="h-5 w-5 text-primary" />Testes e simulações<ChevronDown className="ml-auto h-4 w-4 text-muted-foreground" /></summary>
                <div className="mt-6 space-y-8"><SimuladorCenarios cfg={d.cfg} /><TesteReal cfg={d.cfg} /></div>
              </details>
            </TabsContent>
          </Tabs>
          <p className="text-xs text-muted-foreground">Passo a passo para derrubar golpes, lista negra e radar de páginas falsas ficam em Inteligência do Golpe.</p>
        </>
      )}
    </AdminShell>
  );
}

function Check({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  return <li className="flex items-start gap-2"><span className={ok ? "text-primary" : "text-muted-foreground"}>{ok ? "✓" : "○"}</span><span>{children}</span></li>;
}

function AntiHacker({ cfg, moderador, espiao }: { cfg: GuardConfig; moderador: boolean; espiao: boolean }) {
  const save = useServerFn(saveGuardConfig);
  const qc = useQueryClient();
  const [closed, setClosed] = useState(cfg.closedGroups.join("\n"));
  useEffect(() => setClosed(cfg.closedGroups.join("\n")), [cfg]);
  const on = cfg.enabled && cfg.antiHacker;
  async function salvar(patch: Partial<GuardConfig>) {
    try {
      await save({ data: { ...cfg, ...patch } });
      toast.success("Salvo");
      qc.invalidateQueries({ queryKey: ["admin", "guard"] });
    } catch (e) { toast.error(String(e)); }
  }
  return (
    <AdminCard className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border pb-3">
        <div>
          <p className="text-lg font-bold">Anti-Hacker</p>
          <p className="text-sm text-muted-foreground">Proteção contra mensagens não autorizadas em grupos fechados</p>
        </div>
        <Button variant={on ? "default" : "outline"} onClick={() => salvar(on ? { antiHacker: false } : { antiHacker: true, enabled: true })}>
          {on ? "Ativado" : "Desativado"}
        </Button>
      </div>
      <p className="text-sm">Protege os grupos contra golpistas que conseguem enviar mensagens enquanto o grupo está fechado.</p>
      <div>
        <p className="mb-1 text-sm font-semibold">Antes de ativar:</p>
        <ul className="space-y-1 text-sm">
          <Check ok={espiao}>SPY conectado e como membro dos grupos monitorados.</Check>
          <Check ok={moderador}>ADMIN conectado e como administrador dos grupos monitorados.</Check>
          <Check ok={true}>Equipe autorizada conferida em Configurações. Números do verificador comercial também são reconhecidos.</Check>
        </ul>
      </div>
      <div>
        <p className="mb-1 text-sm font-semibold">Ao ativar:</p>
        <ul className="space-y-1 text-sm">
          <Check ok={true}>Grupo fechado + mensagem de fora da equipe é classificada como golpe.</Check>
          <Check ok={true}>As ações seguem o modo escolhido: {MODES.find((m) => m.id === cfg.mode)?.label}. Falhas precisam de revisão no painel.</Check>
          <Check ok={true}>Números bloqueados na lista negra são verificados ao entrar nos grupos monitorados.</Check>
        </ul>
      </div>
      <p className="border-l-2 border-primary pl-4 text-sm text-muted-foreground">Em grupos abertos, esta regra não atua. As proteções contra links não permitidos, mensagens escondidas e edições continuam conforme suas configurações. A palavra “Pix” isolada apenas gera suspeita.</p>
      <div className="rounded-lg border border-border p-3 text-sm">
        <p className="font-semibold">Proteção por reações</p>
        <p className="text-muted-foreground">Reações só podem acionar proteção sobre mensagens já registradas. Mensagens antigas ou desconhecidas não são prova de golpe. Identificadores internos do WhatsApp não devem ser usados como telefone para expulsar ou bloquear.</p>
      </div>
      <label className="block text-sm">
        <span className="font-semibold">Grupos fechados (reserva)</span>
        <span className="block text-xs text-muted-foreground">O robô pergunta ao WhatsApp se o grupo está fechado. Se não conseguir, usa esta lista: um ID de grupo por linha.</span>
        <Textarea rows={3} value={closed} onChange={(e) => setClosed(e.target.value)} className="mt-1 font-mono text-xs" />
      </label>
      <Button variant="outline" onClick={() => salvar({ closedGroups: lines(closed) })}>Salvar grupos fechados</Button>
    </AdminCard>
  );
}

function Conexao({ papel, nome, desc, st }: { papel: "moderador" | "espiao"; nome: string; desc: string; st: { connected: boolean; configured: boolean; detail: string } }) {
  const getQr = useServerFn(getGuardQr);
  const disc = useServerFn(disconnectGuard);
  const qc = useQueryClient();
  const [aberto, setAberto] = useState(false);
  const qr = useQuery({ queryKey: ["admin", "guard", "qr", papel], queryFn: () => getQr({ data: { papel } }), enabled: aberto && !st.connected, refetchInterval: 15000 });
  useEffect(() => { if (qr.data?.connected) { setAberto(false); qc.invalidateQueries({ queryKey: ["admin", "guard"] }); } }, [qr.data?.connected, qc]);
  return (
    <AdminCard className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="font-bold">{nome}</p>
          <p className="text-xs text-muted-foreground">Conta vinculada por QR code</p>
        </div>
        <span className={`rounded px-2 py-0.5 text-xs font-bold ${st.connected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>{st.connected ? "Conectado" : "Desconectado"}</span>
      </div>
      <p className="text-sm text-muted-foreground">{desc}</p>
      {!st.configured ? (
        <p className="text-sm text-destructive">Conexão ainda não cadastrada.</p>
      ) : st.connected ? (
        <Button variant="outline" size="sm" onClick={async () => { await disc({ data: { papel } }); qc.invalidateQueries({ queryKey: ["admin", "guard"] }); }}>Desconectar</Button>
      ) : (
        <>
          <Button size="sm" onClick={() => setAberto(true)}>Conectar</Button>
          {aberto && (
            <div className="space-y-2 rounded-lg border border-border p-3 text-sm">
              {qr.isLoading && <p className="text-muted-foreground">Gerando QR code...</p>}
              {qr.data?.qr ? <img src={qr.data.qr} alt={`QR code para conectar ${nome}`} className="mx-auto h-56 w-56 rounded bg-card" decoding="async" /> : !qr.isLoading && <p className="text-muted-foreground">QR code indisponível. {qr.data?.detail}</p>}
              <ol className="list-decimal space-y-1 pl-5 text-xs text-muted-foreground">
                <li>No celular de {nome.split("· ")[1]}, abra o WhatsApp.</li>
                <li>Toque em Configurações (ou nos 3 pontinhos) → Aparelhos conectados → Conectar aparelho.</li>
                <li>Aponte a câmera para o código. Ele se renova a cada 15 segundos.</li>
              </ol>
            </div>
          )}
        </>
      )}
    </AdminCard>
  );
}

function copiar(text: string, el?: HTMLTextAreaElement | null) {
  const ok = () => toast.success("Copiado");
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).then(ok, () => fallback());
  } else fallback();
  function fallback() {
    if (!el) return toast.error("Selecione o texto e copie com Ctrl+C");
    el.focus(); el.select();
    try { document.execCommand("copy"); ok(); } catch { toast.error("Selecione o texto e copie com Ctrl+C"); }
  }
}

function CopyBox({ label, text, rows = 8 }: { label: string; text: string; rows?: number }) {
  const [el, setEl] = useState<HTMLTextAreaElement | null>(null);
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-muted-foreground">{label}</span>
        <Button size="sm" variant="outline" onClick={() => copiar(text, el)}><Copy className="mr-1 h-3.5 w-3.5" /> Copiar</Button>
      </div>
      <Textarea ref={setEl} readOnly rows={rows} value={text} onFocus={(e) => e.currentTarget.select()} className="font-mono text-xs" />
    </div>
  );
}

function Config({ cfg }: { cfg: GuardConfig }) {
  const save = useServerFn(saveGuardConfig);
  const qc = useQueryClient();
  const [c, setC] = useState(cfg);
  const [txt, setTxt] = useState({ groups: "", allowedDomains: "", extraAllowedNumbers: "", keywords: "" });
  useEffect(() => {
    setC(cfg);
    setTxt({ groups: cfg.groups.join("\n"), allowedDomains: cfg.allowedDomains.join("\n"), extraAllowedNumbers: cfg.extraAllowedNumbers.join("\n"), keywords: cfg.keywords.join("\n") });
  }, [cfg]);
  const [saving, setSaving] = useState(false);

  async function onSave() {
    setSaving(true);
    try {
      await save({ data: { ...c, groups: lines(txt.groups), allowedDomains: lines(txt.allowedDomains), extraAllowedNumbers: lines(txt.extraAllowedNumbers).map((n) => n.replace(/\D/g, "")), keywords: lines(txt.keywords) } });
      toast.success("Configurações salvas");
      qc.invalidateQueries({ queryKey: ["admin", "guard"] });
    } catch (e) {
      toast.error(String(e));
    } finally {
      setSaving(false);
    }
  }

  const area = (k: keyof typeof txt, label: string, hint: string) => (
    <label className="block text-sm">
      <span className="font-semibold">{label}</span>
      <span className="block text-xs text-muted-foreground">{hint}</span>
      <Textarea rows={4} value={txt[k]} onChange={(e) => setTxt({ ...txt, [k]: e.target.value })} className="mt-1 font-mono text-xs" />
    </label>
  );

  return (
    <AdminCard className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant={c.enabled ? "default" : "outline"} onClick={() => setC({ ...c, enabled: !c.enabled })}>
          {c.enabled ? "Vigilância ligada" : "Vigilância desligada"}
        </Button>
        {MODES.map((m) => (
          <Button key={m.id} variant={c.mode === m.id ? "default" : "outline"} onClick={() => setC({ ...c, mode: m.id })} title={m.desc}>
            {m.label}
          </Button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">Só mensagens classificadas como GOLPE sofrem ação automática; SUSPEITO apenas gera alerta.</p>
      <GruposSeletor
        groups={c.groups}
        teamGroupId={c.teamGroupId}
        testGroups={c.testGroups}
        onChange={(groups, teamGroupId, testGroups = c.testGroups) => { setC({ ...c, groups, teamGroupId, testGroups }); setTxt({ ...txt, groups: groups.join("\n") }); }}
      />
      <div className="grid gap-3 md:grid-cols-2">
        {area("allowedDomains", "Domínios permitidos", "FEO principal e checkouts Eduzz. Para adicionar outro domínio, coloque-o em uma nova linha, sem https:// nem caminho de página.")}
        {area("keywords", "Palavras de risco", "Uma por linha.")}
      </div>
      <Button onClick={onSave} disabled={saving}>{saving ? "Salvando..." : "Salvar"}</Button>
    </AdminCard>
  );
}


function GruposSeletor({ groups, teamGroupId, testGroups, onChange }: { groups: string[]; teamGroupId: string; testGroups: string[]; onChange: (groups: string[], teamGroupId: string, testGroups?: string[]) => void }) {
  const list = useServerFn(listGuardGroups);
  const q = useQuery({ queryKey: ["admin", "guard", "grupos"], queryFn: () => list(), staleTime: 60_000 });
  const [busca, setBusca] = useState("");
  const [soAdmin, setSoAdmin] = useState(true);
  const todos = q.data ?? [];
  const visiveis = todos.filter((g) => (!soAdmin || g.isAdmin !== false) && g.name.toLowerCase().includes(busca.toLowerCase()));
  const toggle = (id: string) => onChange(groups.includes(id) ? groups.filter((g) => g !== id) : [...groups, id], teamGroupId);
  const toggleTeste = (id: string) => onChange(groups, teamGroupId, testGroups.includes(id) ? testGroups.filter((g) => g !== id) : [...testGroups, id]);
  const adminIds = todos.filter((g) => g.isAdmin && g.id !== teamGroupId).map((g) => g.id);
  const desconhecidos = groups.filter((id) => todos.length > 0 && !todos.some((g) => g.id === id));

  return (
    <div className="space-y-3 rounded-lg border border-border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-semibold">Grupos do ADMIN</p>
          <p className="text-xs text-muted-foreground">Para cada grupo: <b>Monitorar</b> (FEO Guard ativo), <b>Alertas</b> (grupo interno que recebe os avisos) ou <b>Teste</b> (libera os marcadores #oculta e #fechado). Nunca marque um grupo real como Teste.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => q.refetch()} disabled={q.isFetching}>{q.isFetching ? "Buscando..." : "Atualizar lista"}</Button>
          <Button size="sm" variant="outline" onClick={() => onChange(Array.from(new Set([...groups, ...adminIds])), teamGroupId)}>Marcar todos em que é admin</Button>
          <Button size="sm" variant="outline" onClick={() => onChange([], teamGroupId)}>Limpar</Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar grupo" className="max-w-xs" />
        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={soAdmin} onChange={(e) => setSoAdmin(e.target.checked)} /> Só grupos em que o ADMIN é administrador</label>
        <span className="text-xs text-muted-foreground">{groups.length} ativos · {todos.length} grupos encontrados</span>
      </div>
      {groups.length === 0 && <p className="text-xs font-semibold text-warning">Nenhum grupo marcado: ADMIN e SPY não monitoram nenhum grupo.</p>}
      {q.isLoading && <p className="text-sm text-muted-foreground">Carregando grupos...</p>}
      {q.error && <p className="text-sm text-destructive">Não foi possível listar os grupos. Confira se o ADMIN está conectado.</p>}
      <div className="max-h-[420px] overflow-y-auto rounded-md border border-border">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-card text-xs uppercase text-muted-foreground">
            <tr><th className="p-2 text-left">Monitorar</th><th className="p-2 text-left">Grupo</th><th className="p-2 text-center">Membros</th><th className="p-2 text-center">ADMIN</th><th className="p-2 text-center">Alertas</th><th className="p-2 text-center">Teste</th></tr>
          </thead>
          <tbody>
            {visiveis.map((g) => (
              <tr key={g.id} className="border-t border-border">
                <td className="p-2"><input type="checkbox" checked={groups.includes(g.id)} onChange={() => toggle(g.id)} aria-label={`Ativar em ${g.name}`} /></td>
                <td className="p-2">
                  <p className="font-medium">{g.name}</p>
                  <p className="font-mono text-[10px] text-muted-foreground">{g.id}{g.fechado ? " · fechado" : ""}</p>
                </td>
                <td className="p-2 text-center">{g.membros ?? "·"}</td>
                <td className="p-2 text-center text-xs">{g.isAdmin == null ? "?" : g.isAdmin ? <span className="rounded px-2 py-0.5 font-semibold guard-tag-info">Admin</span> : <span className="text-muted-foreground">Membro</span>}</td>
                <td className="p-2 text-center"><input type="radio" name="team-group" checked={teamGroupId === g.id} onChange={() => onChange(groups.filter((x) => x !== g.id), g.id, testGroups.filter((x) => x !== g.id))} aria-label={`Enviar alertas para ${g.name}`} /></td>
                <td className="p-2 text-center"><input type="checkbox" disabled={teamGroupId === g.id} checked={testGroups.includes(g.id)} onChange={() => toggleTeste(g.id)} aria-label={`Grupo de teste: ${g.name}`} /></td>
              </tr>
            ))}
            {!q.isLoading && visiveis.length === 0 && <tr><td colSpan={6} className="p-4 text-center text-xs text-muted-foreground">Nenhum grupo.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        Grupo interno dos alertas: <strong>{todos.find((g) => g.id === teamGroupId)?.name ?? (teamGroupId || "nenhum")}</strong>
        {teamGroupId && <button type="button" className="ml-2 underline" onClick={() => onChange(groups, "")}>remover</button>}
        {` · Teste: ${testGroups.length ? testGroups.map((id) => todos.find((g) => g.id === id)?.name ?? id).join(", ") : "nenhum"}`}
        {desconhecidos.length > 0 && ` · ${desconhecidos.length} grupo(s) marcado(s) em que o ADMIN não está mais`}
      </p>
      <p className="text-xs text-muted-foreground">ADMIN e SPY monitoram somente os mesmos grupos marcados acima. O FEO Guard só apaga e remove onde o ADMIN é administrador. Clique em Salvar abaixo para aplicar.</p>
    </div>
  );
}
